/**
 * Stack - vertical layout primitive (stack-first philosophy).
 *
 * Children flow top-to-bottom with a uniform gap between them. This is
 * the DEFAULT composition unit in the system: any page or section is a
 * Stack of blocks unless explicitly told otherwise. When designers need
 * horizontal placement (KPI grid, action-toolbar, identity + actions),
 * they wrap in Row explicitly - thats a deliberate choice, not the default.
 *
 * Geometry:
 *   - gap from spacing tokens (xs / s / m / l / xl / ...). Default m.
 *   - alignX (cross-axis): start / center / end / stretch. Default stretch.
 *
 * Width: 100 percent of container by default. Stack contract is "vertical
 * block that fills its container regardless of parent alignment", which
 * keeps nested Stacks from collapsing under parent alignItems!=stretch.
 *
 * @format
 */

import {forwardRef, type ElementType, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {SPACING_TOKEN_NAMES, spacingPx, type SpacingTokenName} from '../spacing';

export const STACK_ALIGN_X = ['stretch', 'start', 'center', 'end'] as const;
export type StackAlignX = (typeof STACK_ALIGN_X)[number];

export interface StackProps {
  /** Gap between children, from spacing tokens. Default m (16px). */
  gap?: SpacingTokenName;
  /** Cross-axis (horizontal) alignment. Default stretch. */
  alignX?: StackAlignX;
  /** Render as a different semantic element (section, nav, etc.). */
  as?: ElementType;
  className?: string;
  children?: ReactNode;
}

const ALIGN_MAP: Record<StackAlignX, string> = {
  stretch: 'stretch',
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
};

const Root = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$gap: SpacingTokenName; $alignX: StackAlignX}>(({$gap, $alignX}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacingPx($gap),
  alignItems: ALIGN_MAP[$alignX],
  minWidth: 0,
  // Stack fills its container by default. Without this, a Stack nested
  // inside another Stack whose alignX is not stretch would shrink to
  // max-content and collapse any fullWidth descendants. Explicit 100
  // percent locks down the primitive contract.
  width: '100%',
}));

export const Stack = forwardRef<HTMLDivElement, StackProps>(function Stack(
  {gap = 'm', alignX = 'stretch', as, className, children},
  ref,
) {
  return (
    <Root ref={ref} as={as} $gap={gap} $alignX={alignX} className={className}>
      {children}
    </Root>
  );
});

export {SPACING_TOKEN_NAMES as STACK_GAP_TOKENS};
